import { useEffect, useState } from "react";
import {
  adminListTemplates, adminCreateTemplate, adminUpdateTemplate,
  adminDeleteTemplate, adminDuplicateTemplate, adminPreviewTemplate,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Copy, Trash2, Eye, Save, Braces, Search, Mail,
} from "lucide-react";

/** Chip listing every {{var}} we support. Staff can click to insert. */
function VariablePalette({ availableVars, onInsert }) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {availableVars.map(v => (
        <button
          key={v}
          onClick={() => onInsert(`{{${v}}}`)}
          className="px-2 py-0.5 text-[11px] border border-stone-200 bg-stone-50 hover:border-ccc-purple hover:text-ccc-purple font-mono"
          data-testid={`var-${v}`}
        >
          {`{{${v}}}`}
        </button>
      ))}
    </div>
  );
}

function TemplateEditor({ open, initial, availableVars, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { name: "", subject: "", html: "", description: "", category: "" });
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setForm(initial || { name: "", subject: "", html: "", description: "", category: "" });
    setPreview(null);
  }, [initial]);

  const isEdit = !!initial?.id;
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const insertIntoBody = (token) => set("html", (form.html || "") + " " + token);

  const save = async () => {
    if (!form.name || !form.subject || !form.html) { toast.error("Name, subject, and body required"); return; }
    try {
      const r = isEdit
        ? await adminUpdateTemplate(initial.id, form)
        : await adminCreateTemplate(form);
      toast.success(isEdit ? "Template updated" : "Template created");
      onSaved(r);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const doPreview = async () => {
    try {
      const r = await adminPreviewTemplate(initial?.id || "draft", { subject: form.subject, html: form.html });
      setPreview(r);
    } catch { toast.error("Preview failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ccc-plum">
            {isEdit ? "Edit template" : "New template"}
          </DialogTitle>
          <DialogDescription>
            Use <code className="text-xs bg-stone-100 px-1">{"{{first_name}}"}</code>, <code className="text-xs bg-stone-100 px-1">{"{{parish}}"}</code>, etc. Unknown placeholders are left as-is.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 flex-1 overflow-hidden">
          {/* Left: editor */}
          <div className="overflow-y-auto space-y-3 pr-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Name *</label>
              <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="tpl-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Category</label>
              <Input value={form.category || ""} onChange={e => set("category", e.target.value)} placeholder="newsletter / advocacy / event" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Subject *</label>
              <Input value={form.subject} onChange={e => set("subject", e.target.value)} data-testid="tpl-subject"
                placeholder="Take action today, {{first_name}}" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Body (HTML) *</label>
              <VariablePalette availableVars={availableVars} onInsert={insertIntoBody} />
              <Textarea value={form.html} onChange={e => set("html", e.target.value)} rows={14}
                className="font-mono text-xs" data-testid="tpl-html"
                placeholder="<p>Dear {{first_name}},</p>\n<p>Your parish {{parish}} is important to us.</p>" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Description</label>
              <Input value={form.description || ""} onChange={e => set("description", e.target.value)}
                placeholder="Where and when to use this template" />
            </div>
          </div>

          {/* Right: preview */}
          <div className="overflow-y-auto border-l border-stone-100 pl-4">
            <div className="flex justify-between items-center mb-2">
              <div className="text-xs uppercase tracking-widest text-ccc-purple">Live preview</div>
              <Button size="sm" variant="outline" onClick={doPreview} data-testid="btn-preview-template">
                <Eye size={12} className="mr-1"/>Render
              </Button>
            </div>
            {preview ? (
              <>
                <div className="text-xs text-stone-600 mb-2">
                  <span className="uppercase tracking-widest">Subject</span>{" "}
                  <span className="font-medium text-stone-800" data-testid="tpl-preview-subject">{preview.subject}</span>
                </div>
                <iframe
                  srcDoc={preview.html}
                  title="template preview"
                  className="w-full h-96 border border-stone-200 bg-white"
                  sandbox=""
                  data-testid="tpl-preview-iframe"
                />
                <div className="text-[11px] text-stone-500 mt-2">
                  Rendered with sample contact: {preview.sample_contact?.name || "—"}
                </div>
              </>
            ) : (
              <div className="text-xs text-stone-400 italic p-8 text-center">Click Render to preview with a sample contact.</div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-stone-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="btn-save-template"><Save size={14} className="mr-1.5"/>Save template</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [vars, setVars] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // template obj or {} for new
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListTemplates();
      setTemplates(r.templates || []);
      setVars(r.available_vars || []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const dup = async (t) => {
    try { await adminDuplicateTemplate(t.id); toast.success("Duplicated"); load(); }
    catch { toast.error("Duplicate failed"); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await adminDeleteTemplate(t.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const filtered = templates.filter(t =>
    !q.trim() || (t.name + " " + (t.category || "") + " " + (t.subject || ""))
      .toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="admin-email-templates-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-ccc-gold">Email</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Email Templates.</h1>
          <div className="text-sm text-stone-600 mt-1 max-w-2xl">
            Reusable email templates with variables. Use them from the Campaigns page to send personalized broadcasts to any segment.
          </div>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="btn-new-template">
          <Plus size={14} className="mr-1.5"/>New template
        </Button>
      </div>

      <div className="mb-4 relative w-72">
        <Search size={14} className="absolute left-2.5 top-2.5 text-stone-400"/>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search templates…"
          className="pl-8 h-9" data-testid="templates-search"/>
      </div>

      <div className="bg-white border border-stone-200">
        {loading && <div className="p-8 text-center text-stone-400 text-sm">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center" data-testid="empty-templates">
            <Mail size={36} className="mx-auto text-stone-300 mb-3"/>
            <div className="text-stone-500 text-sm">No templates yet. Create one to save time on your next broadcast.</div>
          </div>
        )}
        {!loading && filtered.map(t => (
          <div key={t.id} className="border-b border-stone-100 p-4 hover:bg-stone-50 flex items-center gap-4"
            data-testid={`template-row-${t.id}`}>
            <div className="w-10 h-10 rounded-full bg-ccc-plum/10 text-ccc-plum flex items-center justify-center"><Mail size={16}/></div>
            <button onClick={() => setEditing(t)} className="flex-1 text-left min-w-0">
              <div className="text-sm font-medium text-stone-800">{t.name}</div>
              <div className="text-xs text-stone-500 truncate">{t.subject}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {t.category && <span className="text-[10px] uppercase tracking-widest bg-stone-100 text-stone-600 px-1.5 py-0.5">{t.category}</span>}
                {(t.detected_vars || []).map(v => (
                  <span key={v} className="text-[10px] font-mono text-ccc-purple bg-ccc-purple/5 px-1.5 py-0.5 inline-flex items-center gap-0.5">
                    <Braces size={9}/>{v}
                  </span>
                ))}
              </div>
            </button>
            <button onClick={() => dup(t)} className="text-stone-400 hover:text-ccc-purple" aria-label="Duplicate" data-testid={`btn-duplicate-${t.id}`}>
              <Copy size={14}/>
            </button>
            <button onClick={() => del(t)} className="text-stone-400 hover:text-red-600" aria-label="Delete" data-testid={`btn-delete-${t.id}`}>
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
      </div>

      <TemplateEditor
        open={!!editing || creating}
        initial={editing}
        availableVars={vars}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={load}
      />
    </div>
  );
}
